<template>
  <div class="parent-chat-window" :style="chatWindowContainerStyle">
    <!-- Agent Online & Not Chatting -->
    <template v-if="agentIsOnline && !initiatedChat">
      <div class="chat-window" :style="chatWindowStyle">
        <ChatWindowHeader @close-chat="closeChat" :title="`Chat with ${dealerName}`"></ChatWindowHeader>
        <div class="chat-window-padding form-overflow">
          <p class="suggestion-text">Have a Question? Chat with us.</p>
          <BoxWithShadow>
            <template v-slot:content>
              <div class="form-input-wrapper">
                <FormFieldLabel text="Your First and Last Name" :required="true"></FormFieldLabel>
                <FormTextInput
                  v-model="contactForm.name"
                  :error="contactFormValidations.name.dirty && contactFormValidations.name.valid === false"
                  :error-message="contactFormValidations.name.reason"
                  @input="contactFormInputEvent('name')"
                  @blur="validateContactFormByKey('name')"
                ></FormTextInput>
              </div>
              <div class="form-input-wrapper">
                <FormFieldLabel text="Email" :required="true"></FormFieldLabel>
                <FormTextInput
                  v-model="contactForm.email"
                  :error="contactFormValidations.email.dirty && contactFormValidations.email.valid === false"
                  :error-message="contactFormValidations.email.reason"
                  @input="contactFormInputEvent('email')"
                  @blur="validateContactFormByKey('email')"
                ></FormTextInput>
              </div>
              <div class="form-input-wrapper">
                <FormSubmitButton @submit="submitUserForm()" text="Submit" :loading="submitted"></FormSubmitButton>
              </div>
              <div class="d-flex center-horizontal-flex" v-if="!tiChatGuestDisabled" v-show="!submitted">
                <button class="guest-chat-link" @click="submitGuestForm()">Continue as Guest</button>
              </div>
            </template>
          </BoxWithShadow>
        </div>
      </div>
    </template>
    <!-- /Agent Online & Not Chatting -->

    <!-- Agent Offline -->
    <template v-if="agentIsOnline === false && leadSubmitted === false">
      <div class="chat-window" :style="chatWindowStyle">
        <ChatWindowHeader @close-chat="closeChat" :title="`Leave a Message for ${dealerName}`"></ChatWindowHeader>
        <div class="form-overflow">
          <div class="chat-window-padding">
            <p class="suggestion-text">
              We are currently unavailable. Please enter your contact information below to request a reply. We look
              forward to assisting you.
            </p>
            <BoxWithShadow>
              <template v-slot:content>
                {{ offlineContactFormErrorsComputed }}
                <div class="form-input-wrapper">
                  <FormFieldLabel text="First Name" :required="true"></FormFieldLabel>
                  <FormTextInput
                    v-model="offlineContactForm.firstName"
                    :error="
                      offlineContactFormValidations.firstName.dirty &&
                      offlineContactFormValidations.firstName.valid === false
                    "
                    :error-message="offlineContactFormValidations.firstName.reason"
                    @input="offlineContactFormInputEvent('firstName')"
                    @blur="validateOfflineContactFormByKey('firstName')"
                    offlineFormKey="firstName"
                  ></FormTextInput>
                </div>
                <div class="form-input-wrapper">
                  <FormFieldLabel text="Last Name" :required="true"></FormFieldLabel>
                  <FormTextInput
                    v-model="offlineContactForm.lastName"
                    :error="
                      offlineContactFormValidations.lastName.dirty &&
                      offlineContactFormValidations.lastName.valid === false
                    "
                    :error-message="offlineContactFormValidations.lastName.reason"
                    @input="offlineContactFormInputEvent('lastName')"
                    @blur="validateOfflineContactFormByKey('lastName')"
                  ></FormTextInput>
                </div>
                <div class="form-input-wrapper">
                  <FormFieldLabel text="Email" :required="true"></FormFieldLabel>
                  <FormTextInput
                    v-model="offlineContactForm.email"
                    :error="
                      offlineContactFormValidations.email.dirty && offlineContactFormValidations.email.valid === false
                    "
                    :error-message="offlineContactFormValidations.email.reason"
                    @input="offlineContactFormInputEvent('email')"
                    @blur="validateOfflineContactFormByKey('email')"
                    type="email"
                  ></FormTextInput>
                </div>
                <div class="form-input-wrapper">
                  <FormFieldLabel text="Phone Number"></FormFieldLabel>
                  <FormTextInput
                    v-model="offlineContactForm.phoneNumber"
                    :error="
                      offlineContactFormValidations.phoneNumber.dirty &&
                      offlineContactFormValidations.phoneNumber.valid === false
                    "
                    :error-message="offlineContactFormValidations.phoneNumber.reason"
                    @input="offlineContactFormInputEvent('phoneNumber')"
                    @blur="validateOfflineContactFormByKey('phoneNumber')"
                    type="tel"
                  ></FormTextInput>
                </div>
                <div class="form-input-wrapper">
                  <FormFieldLabel text="Message"></FormFieldLabel>
                  <FormTextArea v-model="offlineContactForm.message"></FormTextArea>
                </div>
                <div class="form-input-wrapper">
                  <FormSubmitButton
                    @submit="submitOfflineContactForm"
                    text="Submit"
                    :loading="submitted"
                  ></FormSubmitButton>
                </div>
              </template>
            </BoxWithShadow>
          </div>
        </div>
      </div>
    </template>
    <!-- /Agent Offline -->

    <!-- Chat -->
    <template v-if="agentIsOnline && initiatedChat">
      <div class="chat-window" :style="chatWindowStyle">
        <ChatWindowHeader @close-chat="closeChat" :title="`${dealerName}`"></ChatWindowHeader>
        <div class="messages">
          <div class="chat-window-padding">
            <ChatMessage :message="theme.chat.initialMessage.value" :show-avatar="true" />
            <template v-for="(message, index) in chatMessages">
              <div :key="index">
                <div v-if="chatUtil.isAuthorSelf(message.state.author)">
                  <ChatMessage
                    :message="message.state.body"
                    :outgoing-message="true"
                    :timestamp="+message.state.timestamp"
                  />
                </div>
                <div v-else>
                  <ChatMessage
                    :message="message.state.body"
                    :show-avatar="true"
                    :timestamp="+message.state.timestamp"
                  />
                </div>
              </div>
            </template>
            <div v-if="conversationState === 'inactive' || conversationState === 'closed'" class="info-bar">
              <p class="info-text">{{ dealerName }} has ended the chat.</p>
            </div>
            <div v-if="showTypingIndicator">
              <ChatMessage :show-typing-indicator="true" :show-avatar="true" />
            </div>
          </div>
        </div>
        <div class="d-flex text-message-input-wrapper">
          <span
            ref="messageInput"
            class="textarea no-outline"
            role="textbox"
            contenteditable
            placeholder="Write a message..."
            @keydown="handleKeyDown"
            @keyup="handleKeyUp"
          >
          </span>
          <button class="no-border no-background" @click="messageSend">
            <svg
              data-v-31f7d743=""
              data-v-c241d988=""
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 18 18"
              aria-labelledby="Send"
              role="presentation"
              color="#565867"
            >
              <title data-v-31f7d743="" id="Send" lang="en">Send</title>
              <g data-v-31f7d743="" fill="currentColor">
                <path
                  d="M17.218,2.268L2.477,8.388C2.13,8.535,2.164,9.05,2.542,9.134L9.33,10.67l1.535,6.787c0.083,0.377,0.602,0.415,0.745,0.065l6.123-14.74C17.866,2.46,17.539,2.134,17.218,2.268 M3.92,8.641l11.772-4.89L9.535,9.909L3.92,8.641z M11.358,16.078l-1.268-5.613l6.157-6.157L11.358,16.078z"
                  data-v-31f7d743=""
                ></path>
              </g>
            </svg>
          </button>
        </div>
      </div>
    </template>
    <template v-if="leadSubmitted">
      <div class="chat-window" :style="chatWindowStyle">
        <ChatWindowHeader @close-chat="closeChat" :title="`${dealerName}`"></ChatWindowHeader>
        <div class="messages">
          <div class="chat-window-padding">
            <ChatMessage message="Thanks for your interest!" :show-avatar="true" />
          </div>
        </div>
      </div>
    </template>
    <div @click="closeChat()" :class="this.$styles.launcherMobileClass()">
      <ChatBubble :chat-window-maximized="true" :style="launcherContainer" class="chat-bubble"></ChatBubble>
    </div>
  </div>
</template>

<script>
import ChatWindowHeader from './ChatWindowHeader.vue'
import BoxWithShadow from './utils/BoxWithShadow.vue'
import FormTextInput from './form/FormTextInput.vue'
import FormTextArea from './form/FormTextArea.vue'
import FormSubmitButton from './form/FormSubmitButton.vue'
import FormFieldLabel from './form/FormFieldLabel.vue'
import ChatUtil from '../utils/ChatUtil'
import formFieldValidHelper from '../utils/formFieldValid'
import ChatMessage from './ChatMessage.vue'
import ChatBubble from './ChatBubble.vue'

export default {
  props: {
    dealerId: {
      type: String,
      default: '',
    },
    dealerName: {
      type: String,
      default: 'Someone',
    },
    adId: {
      type: String,
      default: '',
    },
    adUrl: {
      type: String,
      default: '',
    },
    popUpZIndex: {
      type: Number,
      default: 100,
    },
    pageUrlOverride: {
      type: String,
    },
    chatAppUrl: {
      type: String,
      default: '',
    },
    baseUrlOverride: {
      type: String,
    },
    realmId: {
      type: Number,
      default: null,
    },
    linkName: {
      type: String,
      default: 'tchat-onpage-link',
    },
    tiChatGuestDisabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ChatWindowHeader,
    BoxWithShadow,
    FormTextInput,
    FormTextArea,
    FormSubmitButton,
    FormFieldLabel,
    ChatMessage,
    ChatBubble,
  },
  data() {
    return {
      contactForm: {
        name: '',
        email: '',
      },
      contactFormValidations: {
        name: {
          dirty: false,
          valid: null,
          type: 'fullname',
          fieldName: 'Name',
          reason: null,
          required: true,
        },
        email: {
          dirty: false,
          valid: null,
          type: 'email',
          fieldName: 'Email',
          reason: null,
          required: true,
        },
      },
      offlineContactForm: {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        message: '',
      },
      offlineContactFormValidations: {
        firstName: {
          dirty: false,
          valid: null,
          type: 'firstName',
          fieldName: 'First Name',
          reason: null,
          required: true,
        },
        lastName: {
          dirty: false,
          valid: null,
          type: 'lastName',
          fieldName: 'Last Name',
          reason: null,
          required: true,
        },
        email: {
          dirty: false,
          valid: null,
          type: 'email',
          fieldName: 'Email',
          reason: null,
          required: true,
        },
        phoneNumber: {
          dirty: false,
          valid: null,
          type: 'phone',
          fieldName: 'Phone',
          reason: null,
          required: false,
        },
      },
      message: '',
      chatMessages: [],
      initiatedChat: false,
      chatUtil: null,
      agentIsOnline: null,
      submitted: false,
      leadSubmitted: false,
      showTypingIndicator: false,
      conversationState: 'active',
    }
  },
  async mounted() {
    this.chatUtil = new ChatUtil(
      this.chatAppUrl,
      this.dealerId,
      this.getPageUrl(),
      this.getBaseUrl(),
      this.adUrl,
      this.adId,
      this.realmId,
    )
    this.agentIsOnline = await this.chatUtil.isAgentAvailiable()
    if (this.agentIsOnline) {
      await this.chatUtil.createConversation()
      this.$events.triggerEvent('entry-opened', {
        adId: this.adId,
        dealerId: this.dealerId,
        dealerName: this.dealerName,
        linkName: this.linkName,
      })
      return
    }

    this.$events.triggerEvent('lead-form-opened', {
      adId: this.adId,
      dealerId: this.dealerId,
      dealerName: this.dealerName,
      linkName: this.linkName,
    })
  },
  computed: {
    theme() {
      return this.$theme.getActiveTheme()
    },
    chatWindowContainerStyle() {
      return { ...this.$styles.chatWindowContainer(), ...{ 'z-index': this.popUpZIndex } }
    },
    chatWindowStyle() {
      return { ...this.$styles.chatWindow(), ...{ 'z-index': this.popUpZIndex } }
    },
    firstName() {
      let nameArray = this.contactForm.name.split(' ')

      if (nameArray.length >= 1) {
        return nameArray[0]
      }

      return ''
    },
    lastName() {
      let nameArray = this.contactForm.name.split(' ')

      if (nameArray.length >= 2) {
        return nameArray[1]
      }

      return ''
    },
    offlineContactFormErrorsComputed() {
      return this.offlineContactFormErrors
    },
    launcherContainer() {
      return { ...this.$styles.launcherContainer(), ...{ 'z-index': this.popUpZIndex } }
    },
  },
  methods: {
    getPageUrl() {
      return this.pageUrlOverride ? this.pageUrlOverride : window.location.href
    },
    getBaseUrl() {
      return this.baseUrlOverride ? this.baseUrlOverride : window.location.host
    },
    contactFormValid() {
      this.validateContactForm()
      for (const key of Object.keys(this.contactFormValidations)) {
        if (!this.contactFormValidations[key].valid) {
          return false
        }
      }

      return true
    },
    offlineContactFormValid() {
      this.validateOfflineContactForm()
      for (const key of Object.keys(this.offlineContactFormValidations)) {
        if (!this.offlineContactFormValidations[key].valid) {
          return false
        }
      }

      return true
    },
    async submitGuestForm() {
      if (this.submitted) {
        return
      }
      this.submitted = true
      this.$events.triggerEvent('entry-submitted-guest', {
        adId: this.adId,
        dealerId: this.dealerId,
        dealerName: this.dealerName,
        linkName: this.linkName,
      })
      await this.chatUtil.startGuestConversation()
      await this.addListenersToConversation()

      this.initiatedChat = true
      this.$emit('conversation-started', this.chatUtil.conversation.sid)
      this.$events.triggerEvent('chat-opened', {
        adId: this.adId,
        dealerId: this.dealerId,
        dealerName: this.dealerName,
        linkName: this.linkName,
      })
    },
    async submitUserForm() {
      if (this.submitted || !this.contactFormValid()) {
        return
      }
      this.submitted = true
      this.$events.triggerEvent('entry-submitted', {
        adId: this.adId,
        dealerId: this.dealerId,
        dealerName: this.dealerName,
        linkName: this.linkName,
      })
      await this.chatUtil.startUserConversation(this.firstName, this.lastName, this.contactForm.email)
      await this.addListenersToConversation()

      this.initiatedChat = true
      this.$emit('conversation-started', this.chatUtil.conversation.sid)

      this.$events.triggerEvent('chat-opened', {
        adId: this.adId,
        dealerId: this.dealerId,
        dealerName: this.dealerName,
        linkName: this.linkName,
      })
    },
    submitOfflineContactForm() {
      if (this.submitted) {
        return
      }

      if (!this.offlineContactFormValid()) {
        return
      }

      this.submitted = true

      this.chatUtil.submitLeadForm(
        this.offlineContactForm.firstName,
        this.offlineContactForm.lastName,
        this.offlineContactForm.email,
        this.offlineContactForm.phoneNumber,
        this.offlineContactForm.message,
      )

      this.$events.triggerEvent('lead-form-submitted', {
        adId: this.adId,
        dealerId: this.dealerId,
        dealerName: this.dealerName,
        linkName: 'tchat-submit',
      })
      this.$emit('offline-form-submitted')
      this.leadSubmitted = true
    },
    messageSend() {
      if (this.message) {
        this.addMessage()
        this.$refs.messageInput.innerHTML = ''
        this.message = ''
      }
    },
    messageRecieved(message) {
      this.chatMessages.push(message)
    },
    conversationUpdated(event) {
      this.conversationState = event.conversation.channelState.state.current
    },
    async addListenersToConversation() {
      await this.chatUtil.chatService.addListenersToConversation('messageAdded', this.messageRecieved)
      await this.chatUtil.chatService.addListenersToConversation('typingStarted', () => {
        this.showTypingIndicator = true
      })
      await this.chatUtil.chatService.addListenersToConversation('typingEnded', () => {
        this.showTypingIndicator = false
      })
      await this.chatUtil.chatService.addListenersToConversation('updated', this.conversationUpdated)
    },
    addMessage() {
      this.chatUtil.chatService.sendMessage(this.message)
    },
    async handleKeyDown(event) {
      if (event.keyCode === 13 && !event.shiftKey) {
        event.preventDefault()
        return this.messageSend()
      }
    },
    async handleKeyUp(event) {
      if (event.keyCode === 13 && !event.shiftKey) {
        event.preventDefault()
      }
      const text = this.$refs.messageInput.textContent
      if (text && text.length > 0) {
        this.message = text
      }
    },
    contactFormInputEvent(key) {
      this.contactFormValidations[key].dirty = true
    },
    offlineContactFormInputEvent(key) {
      this.offlineContactFormValidations[key].dirty = true
    },
    validateOfflineContactForm() {
      for (const key of Object.keys(this.offlineContactFormValidations)) {
        this.validateOfflineContactFormByKey(key)
      }
    },
    validateContactForm() {
      for (const key of Object.keys(this.contactFormValidations)) {
        this.validateContactFormByKey(key)
      }
    },
    validateOfflineContactFormByKey(key) {
      let validation = this.formFieldValid(
        this.offlineContactForm[key],
        this.offlineContactFormValidations[key].type,
        this.offlineContactFormValidations[key].fieldName,
        this.offlineContactFormValidations[key].required,
      )

      this.offlineContactFormValidations[key].dirty = true
      this.offlineContactFormValidations[key].reason = validation.reason
      this.offlineContactFormValidations[key].valid = validation.valid
    },
    validateContactFormByKey(key) {
      let validation = this.formFieldValid(
        this.contactForm[key],
        this.contactFormValidations[key].type,
        this.contactFormValidations[key].fieldName,
        this.contactFormValidations[key].required,
      )

      this.contactFormValidations[key].dirty = true
      this.contactFormValidations[key].reason = validation.reason
      this.contactFormValidations[key].valid = validation.valid
    },
    formFieldValid(field, type, fieldName, required) {
      return formFieldValidHelper(field, type, fieldName, required)
    },
    closeChat() {
      this.$emit('close-chat')
    },
  },
}
</script>

<style scoped>
.chat-bubble {
  float: right;
}
.parent-chat-window {
  position: fixed;
  right: 25px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: end;
}

.chat-window {
  min-height: 200px;
  flex-grow: 1;
  overflow: hidden;
  width: min(370px, calc(100vw - 50px));
  max-height: 590px;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  -webkit-box-shadow: 0 7px 40px 2px rgb(148 149 150 / 10%);
  box-shadow: 0 7px 40px 2px rgb(148 149 150 / 10%);
  background: #fff;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  -webkit-box-pack: justify;
  -ms-flex-pack: justify;
  border-radius: 10px;
  font-family: Helvetica Neue, Helvetica, Arial, sans-serif;
  -webkit-animation: fadeIn-data-v-08d4c038;
  animation: fadeIn-data-v-08d4c038;
  -webkit-animation-duration: 0.3s;
  animation-duration: 0.3s;
  -webkit-animation-timing-function: ease-in-out;
  animation-timing-function: ease-in-out;
}

.suggestion-text {
  color: #5f5f5f;
  font-size: 14px;
  padding-bottom: 10px;
  text-align: center;
}

.form-overflow {
  overflow-y: auto;
}

.chat-window-padding {
  padding: 20px;
}

.form-input-wrapper {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

button.guest-chat-link {
  background: none;
  border: none;
  cursor: pointer;
  color: #2971ea;
  font-size: 14px;
  margin-top: 5px;
}

.messages {
  height: 80%;
  overflow-y: auto;
  background-size: 100%;
}

.text-message-input-wrapper {
  padding: 15px;
  background: rgb(244, 247, 249);
}

.textarea {
  width: 100%;
  line-height: 20px;
  max-height: 100px;
  overflow-y: auto;
  cursor: text;
}

.text-input-position {
  position: relative;
  -webkit-box-flex: 1;
  -ms-flex: 1 1 auto;
  flex: 1 1 auto;
  width: 1%;
  margin-bottom: 0;
}

.info-bar {
  width: 100%;
  border-top: 1px solid #e5e5e5;
}

.info-text {
  font-size: 14px;
  text-align: center;
  color: #757575;
}

@media screen and (max-width: 850px) {
  .mobile-hide {
    display: none;
  }
}

[contenteditable][placeholder]:empty:before {
  content: attr(placeholder);
  position: absolute;
  color: gray;
  background-color: transparent;
}
</style>
